import React from "react";
import {
  Box,
  Chip,
  Grid2,
  IconButton,
  Paper,
  Stack,
  Theme,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  ContentCopy,
  Pending as PendingIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";
import { PurhcaseRequestDetails } from "../../../../../models/PurchaseRequest/PurchaseRequestDetails";
import {
  formatUrgentPaymentYesLabel,
  getUrgentPaymentWorkflowStatus,
} from "../../../../../utils/purchaseRequestUrgentPayment";
export type PrApprovalTeam = "sourcing" | "finance" | "swageazy";

export const getPrApprovalStatus = (
  pr: PurhcaseRequestDetails,
  type: PrApprovalTeam,
): string => {
  if (type === "sourcing") {
    if (Boolean(pr.sourcing_approval)) return "Approved";
    if (
      pr.rejected_by_sourcing_admin_id !== null &&
      pr.rejected_by_sourcing_admin_id > 0
    ) {
      return "Rejected";
    }
    return "Pending";
  }

  if (type === "finance") {
    if (Boolean(pr.finance_approval)) return "Approved";
    if (
      pr.rejected_by_finance_admin_id !== null &&
      pr.rejected_by_finance_admin_id > 0
    ) {
      return "Rejected";
    }
    return "Pending";
  }

  if (!pr.third_approval_required) return "N/A";
  if (Boolean(pr.swageazy_approval)) return "Approved";
  if (
    pr.rejected_by_swageazy_admin_id !== null &&
    pr.rejected_by_swageazy_admin_id > 0
  ) {
    return "Rejected";
  }
  return "Pending";
};

export const getPrOverallStatus = (pr: PurhcaseRequestDetails): string => {
  if (pr.pr_cancellation_date) return "Cancelled";
  if (
    (Number(pr.rejected_by_sourcing_admin_id) || 0) > 0 ||
    (Number(pr.rejected_by_finance_admin_id) || 0) > 0 ||
    (Number(pr.rejected_by_swageazy_admin_id) || 0) > 0
  ) {
    return "Rejected";
  }
  return "Active";
};

const approvalChipSx = (theme: Theme, status: string) => {
  const c = theme.palette.customColors;
  if (status === "Approved") {
    return {
      height: 22,
      fontSize: "0.68rem",
      fontWeight: 700,
      color: c.successGreen,
      backgroundColor: c.successGreenLightBackground,
      border: `1px solid ${c.successGreenLightBorder}`,
    };
  }
  if (status === "Rejected") {
    return {
      height: 22,
      fontSize: "0.68rem",
      fontWeight: 700,
      color: c.errorRed,
      backgroundColor: theme.palette.blushPink.main,
      border: `1px solid ${c.borderErrorRedColor}`,
    };
  }
  if (status === "N/A") {
    return {
      height: 22,
      fontSize: "0.68rem",
      fontWeight: 700,
      color: c.grayText,
      backgroundColor: c.veryLightGray,
      border: `1px solid ${c.lightGray}`,
    };
  }
  return {
    height: 22,
    fontSize: "0.68rem",
    fontWeight: 700,
    color: theme.palette.orange.dark,
    backgroundColor: c.lightOrange,
    border: `1px solid ${theme.palette.orange.light}`,
  };
};

const prStatusChipLabelSx = {
  px: 0.75,
  py: 0,
  lineHeight: 1.2,
};

const prStatusChipSx = (theme: Theme, status: string) => {
  const c = theme.palette.customColors;
  const compact = {
    height: 22,
    fontSize: "0.68rem",
    fontWeight: 700,
    "& .MuiChip-label": prStatusChipLabelSx,
  };
  if (status === "Active") {
    return {
      ...compact,
      color: c.darkBlue,
      backgroundColor: c.blueLightBackground,
      border: `1px solid ${c.softBorder}`,
    };
  }
  if (status === "Cancelled") {
    return {
      ...compact,
      color: c.errorRed,
      backgroundColor: theme.palette.blushPink.main,
      border: `1px solid ${c.borderErrorRedColor}`,
    };
  }
  return {
    ...compact,
    color: c.errorRed,
    backgroundColor: theme.palette.blushPink.main,
    border: `1px solid ${c.borderErrorRedColor}`,
  };
};

interface PrListCardProps {
  item: PurhcaseRequestDetails;
  onOpenDetails: (item: PurhcaseRequestDetails) => void;
  onOpenOrder: (orderId: number) => void;
  onCopy: (
    event: React.MouseEvent,
    value?: string | number | null,
    label?: string,
  ) => void;
  renderMeta: (label: string, value?: React.ReactNode) => React.ReactNode;
  fieldGridSize: { xs: number; sm: number; md: number; lg: number; xl: number };
}

const PrListCard: React.FC<PrListCardProps> = ({
  item,
  onOpenDetails,
  onOpenOrder,
  onCopy,
  renderMeta,
  fieldGridSize,
}) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);

  const prStatus = getPrOverallStatus(item);
  const orderLabel =
    Number(item.order_id) === -1
      ? "Swageazy Inventory"
      : item.order_id
        ? String(item.order_id)
        : "-";

  const canOpenOrder = Boolean(item.order_id && Number(item.order_id) !== -1);

  const urgentPaymentWorkflowStatus = getUrgentPaymentWorkflowStatus(item);
  const urgentPaymentMeta = (() => {
    if (urgentPaymentWorkflowStatus === "not_required") {
      return "No";
    }
    const isDone = urgentPaymentWorkflowStatus === "done";
    const statusIcon = isDone ? (
      <CheckCircleIcon
        sx={{
          fontSize: 18,
          color: theme.palette.customColors.successGreen,
        }}
      />
    ) : (
      <PendingIcon
        sx={{
          fontSize: 18,
          color: theme.palette.orange.dark,
        }}
      />
    );
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          flexWrap: "wrap",
        }}
      >
        <CustomTypography
          variant="body2"
          fontWeight={600}
          sx={{ fontSize: "0.78rem", lineHeight: 1.2 }}
        >
          {formatUrgentPaymentYesLabel(item.urgent_payment_percentage)}
        </CustomTypography>
        <Tooltip title={isDone ? "Urgent payment Paid" : "Urgent payment pending"}>
          <Box
            component="span"
            sx={{ display: "inline-flex", alignItems: "center" }}
          >
            {statusIcon}
          </Box>
        </Tooltip>
      </Box>
    );
  })();

  return (
    <Paper
      elevation={0}
      onClick={() => onOpenDetails(item)}
      sx={{
        cursor: "pointer",
        borderRadius: "16px",
        overflow: "hidden",
        backgroundColor: colors.surface,
        border: `2px solid ${colors.border}`,
        boxShadow: colors.shadowSoft,
        "&:hover": {
          borderColor: theme.palette.customColors.purpleMain,
        },
      }}
    >
      <Box sx={{ px: { xs: 1, sm: 1.25 }, pt: 1, pb: 0.75 }}>
        <Stack
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
        >
          <Stack direction="row" spacing={0.25} alignItems="center">
            <CustomTypography
              variant="heading7"
              fontWeight={700}
              color={theme.palette.primary.main}
              sx={{
                fontSize: "0.9rem",
                lineHeight: 1.2,
                textDecoration: "underline",
              }}
            >
              PR #{item.id}
            </CustomTypography>
            <Tooltip title="Copy PR ID">
              <IconButton
                size="small"
                onClick={(event) => onCopy(event, item.id, "PR ID")}
                sx={{
                  p: 0.25,
                  color: colors.subtitle,
                  "&:hover": {
                    backgroundColor: colors.surfaceMuted,
                    color: theme.palette.primary.main,
                  },
                }}
              >
                <ContentCopy sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Stack>
          <Chip
            label={prStatus.toUpperCase()}
            size="small"
            sx={prStatusChipSx(theme, prStatus)}
          />
        </Stack>

        <Grid2
          container
          spacing={1.5}
          columns={{ xs: 4, sm: 6, md: 8, lg: 10, xl: 10 }}
          sx={{ mt: 1, mb: 0.5, alignItems: "stretch" }}
        >
          <Grid2 size={fieldGridSize}>
            {renderMeta(
              "Order",
              canOpenOrder ? (
                <Box
                  component="span"
                  onClick={(event: React.MouseEvent) => {
                    event.stopPropagation();
                    onOpenOrder(Number(item.order_id));
                  }}
                  sx={{ cursor: "pointer", display: "inline-flex" }}
                >
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    color={theme.palette.primary.main}
                    sx={{
                      fontSize: "0.78rem",
                      textDecoration: "underline",
                    }}
                  >
                    #{orderLabel}
                  </CustomTypography>
                </Box>
              ) : (
                orderLabel
              ),
            )}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta("PO ID", item.po_ids || "-")}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta("Vendor", item.vendor_name || "-")}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta("Urgent Payment", urgentPaymentMeta)}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta(
              "Expected Delivery",
              item.expected_delivery_date
                ? dayjs(item.expected_delivery_date).format("DD/MM/YYYY")
                : "-",
            )}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta(
              "Sourcing",
              <Chip
                label={getPrApprovalStatus(item, "sourcing").toUpperCase()}
                size="small"
                sx={approvalChipSx(theme, getPrApprovalStatus(item, "sourcing"))}
              />,
            )}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta(
              "Finance",
              <Chip
                label={getPrApprovalStatus(item, "finance").toUpperCase()}
                size="small"
                sx={approvalChipSx(theme, getPrApprovalStatus(item, "finance"))}
              />,
            )}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta(
              "Swageazy",
              <Chip
                label={getPrApprovalStatus(item, "swageazy").toUpperCase()}
                size="small"
                sx={approvalChipSx(theme, getPrApprovalStatus(item, "swageazy"))}
              />,
            )}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta("Created By", item.created_by_adminname || "-")}
          </Grid2>
          <Grid2 size={fieldGridSize}>
            {renderMeta(
              "Created Date",
              item.created_date
                ? dayjs(item.created_date).format("DD/MM/YYYY HH:mm")
                : "-",
            )}
          </Grid2>

        </Grid2>
      </Box>
    </Paper>
  );
};

export default PrListCard;
